import { config } from "./config";
import { sessionStore } from "./state/session";
import { initAuthView, showAuthShell } from "./ui/authView";
import { initAppShell } from "./ui/appShell";
import { byId } from "./ui/dom";
import { handleTopupReturn } from "./ui/topupReturn";
import { logActivity } from "./services/activityService";

function initHeader(): void {
  const envBadge = byId("env-badge");
  envBadge.hidden = !config.useMockServices;

  const connLabel = byId("conn-status-label");
  sessionStore.subscribe((session) => {
    connLabel.textContent = session ? "Signed In" : "Signed Out";
  });
  connLabel.textContent = sessionStore.isAuthenticated ? "Signed In" : "Signed Out";
}

/** In real (Supabase) mode, the Supabase client doesn't persist its own
 *  session (see supabaseClient.ts) — sessionStore is the single source of
 *  truth instead. On a page load where sessionStore already holds a
 *  session, this puts the Supabase client back into the same
 *  authenticated state before any data fetch runs, so RLS sees the right
 *  user rather than an anonymous one. If the stored tokens are stale
 *  (e.g. the refresh token expired), the session is cleared so the person
 *  lands on a fresh login instead of a broken, half-authenticated app. */
async function rehydrateSupabaseSession(): Promise<void> {
  if (config.useMockServices) return;
  const session = sessionStore.current;
  if (!session?.supabaseAccessToken || !session?.supabaseRefreshToken) return;

  try {
    const { getSupabase } = await import("./services/supabaseClient");
    const { error } = await getSupabase().auth.setSession({
      access_token: session.supabaseAccessToken,
      refresh_token: session.supabaseRefreshToken,
    });
    if (error) sessionStore.clear();
  } catch {
    sessionStore.clear();
  }
}

/** Supabase fires a PASSWORD_RECOVERY auth event when the resident lands
 *  back on the app via the link from a password-reset email. Route
 *  straight to the reset-confirm screen when that happens, regardless of
 *  what else is on screen. No-op in mock mode. */
async function watchForPasswordRecovery(authController: { showResetConfirm(): void }): Promise<void> {
  if (config.useMockServices) return;
  const { getSupabase } = await import("./services/supabaseClient");
  getSupabase().auth.onAuthStateChange((event) => {
    if (event === "PASSWORD_RECOVERY") {
      authController.showResetConfirm();
      // The token has been read; drop it from the address bar so a refresh
      // doesn't replay the reset screen.
      window.history.replaceState(null, "", window.location.pathname);
    }
  });
}

async function main(): Promise<void> {
  initHeader();

  const appShell = initAppShell();
  const authController = initAuthView(() => {
    appShell.boot();
    logActivity("login");
    logActivity("session_start");
  });

  void watchForPasswordRecovery(authController);

  // A reset-password email link must always open the "set a new password"
  // screen. Without this, a browser that already holds a saved login would
  // boot straight into the dashboard and the reset link would seem to do
  // nothing (the recovery event and the normal boot race each other).
  const linkParams = window.location.hash + window.location.search;
  if (!config.useMockServices && /type=recovery/.test(linkParams)) {
    sessionStore.clear();
    authController.showResetConfirm();
    return;
  }
  if (!config.useMockServices && /error_code=otp_expired|error=access_denied/.test(linkParams)) {
    sessionStore.clear();
    showAuthShell();
    window.history.replaceState(null, "", window.location.pathname);
    alert("That password reset link has expired or was already used. Please request a new one.");
    return;
  }

  if (sessionStore.isAuthenticated) {
    await rehydrateSupabaseSession();
    if (sessionStore.isAuthenticated) {
      appShell.boot();
      logActivity("session_start");
      if (!config.useMockServices) void handleTopupReturn();
    } else {
      showAuthShell();
    }
  } else {
    showAuthShell();
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => void main());
} else {
  void main();
}
